var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _every from 'lodash/every';
import _some from 'lodash/some';
import _countBy from 'lodash/countBy';
import _shuffle from 'lodash/shuffle';
import _head from 'lodash/head';
import _last from 'lodash/last';
import _nth from 'lodash/nth';
import _orderBy from 'lodash/orderBy';
import _reverse from 'lodash/reverse';
import _indexOf from 'lodash/indexOf';
import _uniq from 'lodash/uniq';
import { observable, action, computed, autorun, makeObservable } from 'mobx';
import CollectionError from './collectionError';
import Utils from './utils';
export { CollectionError };
export default class Collection {
    _pendingRequestCount = 0;
    model;
    isLoaded = false;
    models = observable([]);
    options = {};
    _comparators = [];
    _orders = [];
    _cancelSubscription = () => { };
    _isVirtualCollection = false;
    constructor(model, models = [], options = {}) {
        this.options = options;
        this.model = model;
        this._comparators = options.comparator ? [options.comparator] : [];
        this._orders = options.order ? [options.order] : [];
        this.set(models);
        this.initialize(options);
        makeObservable(this);
    }
    get length() {
        return this.models.length;
    }
    get isEmpty() {
        return this.models.length == 0;
    }
    get isLoading() {
        return this._pendingRequestCount > 0;
    }
    /* istanbul ignore next */
    toString() {
        return `[${this.constructor.name} length=${this.length}${this.isVirtualCollection ? ' virtual' : ''}]`;
    }
    /* istanbul ignore next */
    toJSON() {
        return this.toString();
    }
    createVirtualCollection(filter, options = {}) {
        const virtualCollection = new this.constructor(this.model, [], { ...this.options, ...options });
        virtualCollection.setSubscription(() => {
            const newModels = this.filter(filter);
            const lengthChanged = newModels.length !== virtualCollection.length;
            const modelChanged = lengthChanged || newModels.some((model, index, models) => {
                return virtualCollection.indexOf(model) < 0;
            });
            if (modelChanged) {
                virtualCollection.set(newModels);
            }
            virtualCollection._pendingRequestCount = this._pendingRequestCount;
        });
        return virtualCollection;
    }
    createSubscription(func) {
        return autorun(func);
    }
    setSubscription(func) {
        this.cancelSubscription();
        this._isVirtualCollection = true;
        this._cancelSubscription = this.createSubscription(func);
    }
    cancelSubscription() {
        this._cancelSubscription();
        this._cancelSubscription = () => { };
        this._isVirtualCollection = false;
    }
    throwIfVirtualCollection() {
        // Is this a virtual collection?
        if (this.isVirtualCollection) {
            throw new Error('Virtual collection cannot do that!');
        }
    }
    get isVirtualCollection() {
        return this._isVirtualCollection;
    }
    // API methods
    list(options = {}) {
        return new Promise((resolve, reject) => {
            this.throwIfVirtualCollection();
            const listOptions = this.prepareListOptions(options);
            this._pendingRequestCount++;
            return this.model.connector.list(this, listOptions)
                .then((result) => {
                this.onListSuccess(result, listOptions);
                resolve(this);
            }, (err) => {
                this.onListError(err);
            })
                .catch((err) => {
                reject(err);
            });
        });
    }
    prepareListOptions(options) {
        return options;
    }
    onListSuccess(results, options) {
        this.onSuccess(results);
        this.set(results.items.map((item) => this.createModel(item, { loaded: true, transform: true, collection: this })));
        this.isLoaded = true;
    }
    onListError(err) {
        this.onError('Error listing models', err);
    }
    // Global handlers
    onSuccess(results) {
        this._pendingRequestCount--;
    }
    onError(message, err) {
        this._pendingRequestCount--;
        throw new CollectionError(message + ' -> ' + err.message, err);
    }
    // Models methods
    clear() {
        this.throwIfVirtualCollection();
        this.forEach((model) => model.collection = undefined);
        this.models.clear();
        this.isLoaded = false;
        return this;
    }
    set(models) {
        this.models.replace(models.map((model) => {
            if (!this.isVirtualCollection) {
                model.collection = this;
            }
            return model;
        }));
        this.sort();
        return this;
    }
    add(model) {
        this.throwIfVirtualCollection();
        model.collection = this;
        this.models.push(model);
        this.sort();
        return this;
    }
    remove(model) {
        this.throwIfVirtualCollection();
        model.collection = undefined;
        this.models.remove(model);
        this.sort();
        return this;
    }
    getById(id) {
        return this.find((model) => model.id == id);
    }
    removeById(id) {
        this.throwIfVirtualCollection();
        const model = this.getById(id);
        if (model)
            this.remove(model);
        this.sort();
        return model;
    }
    // Collection methods
    shift() {
        this.throwIfVirtualCollection();
        const model = this.models.shift();
        if (model)
            model.collection = undefined;
        this.sort();
        return model || null;
    }
    unshift(model) {
        this.throwIfVirtualCollection();
        model.collection = this;
        this.models.unshift(model);
        this.sort();
    }
    pop() {
        this.throwIfVirtualCollection();
        const model = this.models.pop();
        if (model)
            model.collection = undefined;
        this.sort();
        return model || null;
    }
    push(model) {
        this.throwIfVirtualCollection();
        model.collection = this;
        this.models.push(model);
        this.sort();
    }
    indexOf(model) {
        const index = _indexOf(this.models, model);
        return index < 0 ? -1 : index;
    }
    map(iteratee) {
        return this.models.map(iteratee);
    }
    reduce(iteratee, initialValue) {
        return this.models.reduce(iteratee, initialValue);
    }
    forEach(iteratee) {
        this.models.forEach(iteratee);
        return this;
    }
    pluck(attribute, defaultValue = undefined) {
        return this.map((model) => model.get(attribute, defaultValue));
    }
    countBy(predicate) {
        return _countBy(this.models, predicate);
    }
    filter(predicate) {
        return _filter(this.models, predicate);
    }
    every(predicate) {
        return _every(this.models, predicate);
    }
    some(predicate) {
        return _some(this.models, predicate);
    }
    find(predicate) {
        return _find(this.models, predicate) || null;
    }
    at(position) {
        return _nth(this.models, position) || null;
    }
    first() {
        return _head(this.models) || null;
    }
    last() {
        return _last(this.models) || null;
    }
    shuffle() {
        this.models.replace(_shuffle(this.models));
        return this;
    }
    distinct(attribute) {
        return _uniq(this.pluck(attribute));
    }
    sortBy(comparator, order = 'asc') {
        this._comparators = [comparator];
        this._orders = [order];
        this.sort();
        return this;
    }
    reverse() {
        this.models.replace(_reverse(this.models));
        return this;
    }
    clone(options = {}) {
        this.throwIfVirtualCollection();
        const collection = new this.constructor(this.model, [], this.options);
        if (options.deep) {
            collection.set(this.models.map((model) => model.clone()));
        }
        else {
            collection.set(this.models.slice());
        }
        return collection;
    }
    sort() {
        if (this._comparators.length > 0 || this._orders.length > 0)
            this.models.replace(_orderBy(this.models, this._comparators, this._orders));
    }
    // *****
    get path() {
        return Utils.format(this.modelPath, this.options.attributes || {});
    }
    get modelPath() {
        return this.model.path;
    }
    initialize(options) {
        // To override
    }
    createModel(item, options) {
        return new this.model(item, options);
    }
}
__decorate([
    observable
], Collection.prototype, "_pendingRequestCount", void 0);
__decorate([
    observable
], Collection.prototype, "isLoaded", void 0);
__decorate([
    computed
], Collection.prototype, "length", null);
__decorate([
    computed
], Collection.prototype, "isEmpty", null);
__decorate([
    computed
], Collection.prototype, "isLoading", null);
__decorate([
    action
], Collection.prototype, "list", null);
__decorate([
    action
], Collection.prototype, "onListSuccess", null);
__decorate([
    action
], Collection.prototype, "onListError", null);
__decorate([
    action
], Collection.prototype, "onSuccess", null);
__decorate([
    action
], Collection.prototype, "onError", null);
__decorate([
    action
], Collection.prototype, "clear", null);
__decorate([
    action
], Collection.prototype, "set", null);
__decorate([
    action
], Collection.prototype, "add", null);
__decorate([
    action
], Collection.prototype, "remove", null);
__decorate([
    action
], Collection.prototype, "shift", null);
__decorate([
    action
], Collection.prototype, "unshift", null);
__decorate([
    action
], Collection.prototype, "pop", null);
__decorate([
    action
], Collection.prototype, "push", null);
__decorate([
    action
], Collection.prototype, "shuffle", null);
__decorate([
    action
], Collection.prototype, "reverse", null);
__decorate([
    action
], Collection.prototype, "sort", null);
//# sourceMappingURL=collection.js.map